/**
 * 手机号「已验证」的唯一判定（2026-10-31）
 * -----------------------------------------------------------------------------
 * 一个号码算不算验过，有**两条来源**，必须一起看：
 *
 *   ① 本地刚验过 —— 学员在本页填码通过，`verifiedFor` 记下通过的**那个号**；
 *      改号即失效，所以存号码而不存布尔值。
 *   ② 账号级免验 —— 表单里的号 == 账号手机号，且账号号已验证过。服务端在
 *      `PHONE-005` 判定里走同一口径（`PhoneVerificationService.isAccountPhoneVerified`），
 *      前端漏了这一条就会出现「已经验过的号还要再验一次」。
 *
 * 为什么单独抽出来：`PhoneVerifyField`（控件自己显示「短信已验证」）与
 * `KycFlow`（卡片胶囊与「下一步」门禁）都要判，两处各写一遍必然漂移 ——
 * 真实事故就是控件按 ② 显示成「已验证」、门禁只认 ①，于是卡片写着「待验证」、
 * 页面上又没有「获取验证码」按钮，学员被卡死在第三步。
 *
 * ⚠️ 比对用**明文**：`profile.phone` 来自 `GET /profile/basic`（仅本人可取），
 * 掩码值 `139****9000` 与真实号码永远不相等，拿它比对会恒为 false。
 */

/** 账号资料里与免验相关的两个字段（`H5ProfileBasic` 的子集，便于单测构造） */
export interface AccountPhoneProfile {
  phone: string | null;
  phoneVerified: boolean;
}

/** 号码归一：非字符串 / 空白统一成空串，避免 `null` 与 `''` 各判一套 */
export function normalizePhoneText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 账号级免验：`profile.phoneVerified === true` 且表单号码与账号号码明文相等。
 *
 * 账号号为空（资料未完善）或表单号为空时一律不成立 —— 空 == 空会被判成
 * 「已验证」，那等于把没填的手机号放行。
 */
export function isAccountPhoneVerified(
  profile: AccountPhoneProfile | null | undefined,
  phone: unknown,
): boolean {
  if (profile?.phoneVerified !== true) return false;
  const accountPhone = normalizePhoneText(profile.phone);
  const target = normalizePhoneText(phone);
  if (!accountPhone || !target) return false;
  return accountPhone === target;
}

export interface PhoneVerifiedInput {
  /** 本地验证通过时锁定的号码（`null` = 本页没验过） */
  verifiedFor: string | null;
  /** 当前表单里的号码 */
  phone: unknown;
  /** 账号资料（拿不到时传 `null`，只按本地态判） */
  profile: AccountPhoneProfile | null | undefined;
}

/**
 * 合并两条来源。`verifiedFor` 存的号码必须与当前号码一致 —— 改号后
 * 服务端标记同样按号码隔离，换号必须重验（这也是不存布尔值的原因）。
 */
export function isPhoneVerified({ verifiedFor, phone, profile }: PhoneVerifiedInput): boolean {
  const target = normalizePhoneText(phone);
  if (verifiedFor !== null && verifiedFor === target) return true;
  return isAccountPhoneVerified(profile, target);
}
